import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown } from 'lucide-react';
import SubscriptionSettings from '@/components/Settings/SubscriptionSettings';

interface ProModalContextType {
  openProModal: (featureName?: string, description?: string) => void;
  closeProModal: () => void;
  openSubscriptionModal: () => void;
  closeSubscriptionModal: () => void;
}

const ProModalContext = createContext<ProModalContextType | undefined>(undefined);

export const ProModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [proModalOpen, setProModalOpen] = useState(false);
  const [proFeatureName, setProFeatureName] = useState<string | undefined>(undefined);
  const [proDescription, setProDescription] = useState<string | undefined>(undefined);
  
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  const openProModal = (featureName?: string, description?: string) => {
    setProFeatureName(featureName);
    setProDescription(description);
    setProModalOpen(true);
  };

  const closeProModal = () => setProModalOpen(false);

  const openSubscriptionModal = () => {
    setSubscriptionModalOpen(true);
  };

  const closeSubscriptionModal = () => setSubscriptionModalOpen(false);

  const handleUpgradeClick = () => {
    closeProModal();
    openSubscriptionModal();
  };

  return (
    <ProModalContext.Provider value={{ openProModal, closeProModal, openSubscriptionModal, closeSubscriptionModal }}>
      {children}
      
      {/* Pro Limits / Feature Golden Modal */}
      <Dialog open={proModalOpen} onOpenChange={setProModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <Crown className="h-5 w-5" />
              Función Exclusiva Pro
            </DialogTitle>
            <DialogDescription className="pt-2 text-base text-foreground/90">
              {proDescription 
                ? proDescription 
                : (proFeatureName ? `El acceso a ${proFeatureName} está reservado para cuentas Pro.` : 'Esta función está reservada para cuentas Pro o Enterprise.')
              }
              <br /><br />
              Actualiza tu plan para desbloquear reportes financieros, exportación CSV, clientes ilimitados y mucho más.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end mt-4">
            <Button variant="outline" onClick={closeProModal}>
              Cerrar
            </Button>
            <Button onClick={handleUpgradeClick} className="bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white border-0 shadow-md">
              Actualizar a Pro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Settings Modal */}
      <Dialog open={subscriptionModalOpen} onOpenChange={setSubscriptionModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background border-border">
          <DialogTitle className="sr-only">Suscripción</DialogTitle>
          <SubscriptionSettings />
        </DialogContent>
      </Dialog>
    </ProModalContext.Provider>
  );
};

export const useProModal = () => {
  const context = useContext(ProModalContext);
  if (context === undefined) {
    throw new Error('useProModal must be used within a ProModalProvider');
  }
  return context;
};
